import { z } from 'zod';
/**
 * What a handset needs in order to trust a Gate Package it was handed.
 *
 * **The circularity this exists to break.** A Gate Package is signed, and the
 * key that verifies it cannot travel inside it: a package that named the key
 * authenticating it would be authenticating itself, and a forged package would
 * name a forged key. So the verification keyring reaches a handset by a
 * different route from the package — over the venue channel the handset already
 * authenticates for every other purpose.
 *
 * The trust chain is:
 *
 * ```
 * gate_package_signing_keys            the cloud's canonical authority
 *   → authenticated cloud→Edge control response
 *   → Edge, which retains it as current trust policy
 *   → the pinned, mutually-authenticated scanner↔Edge channel
 *   → the handset's package verifier
 * ```
 *
 * Nothing in that chain lets a package speak about its own key, and every hop
 * is already authenticated by machinery this tranche did not invent.
 */
/**
 * Whether a signing key may still verify, and whether it may still sign.
 *
 * Three states rather than a boolean, because the middle one is the whole point
 * of rotation: a retired key stopped signing new packages and must keep
 * verifying old ones, or every handset holding a package signed last week would
 * refuse it the moment a rotation happened.
 */
export const GATE_PACKAGE_SIGNING_KEY_STATUSES = [
    /** Signs new packages, and verifies. */
    'ACTIVE',
    /**
     * No longer signs; still verifies.
     *
     * Rotation retires a key and packages already on handsets keep verifying
     * under it until they are replaced — the reason
     * `gate_package_signing_keys` keeps retired rows rather than deleting them.
     */
    'RETIRED',
    /**
     * Must not verify anything, ever again.
     *
     * The one status that withdraws trust rather than narrowing it. A handset
     * holding a package signed by a compromised key must refuse that package,
     * which is why this status has to be able to reach a handset **without** a
     * new package being built: the bytes it needs to reject are bytes it already
     * has.
     */
    'COMPROMISED',
];
export const gatePackageSigningKeyStatusSchema = z.enum(GATE_PACKAGE_SIGNING_KEY_STATUSES);
/**
 * One package-signing key, as the cloud's canonical registry holds it.
 *
 * A projection of `gate_package_signing_keys` and deliberately not a second
 * registry: `keyId`, `algorithm`, `publicKeySpki` and `status` are that table's
 * own columns. **Public material only.** There is no field here that could
 * carry a private half, and there must never be one.
 */
export const gatePackageSigningKeySchema = z.object({
    keyId: z.string().min(1),
    /** `Ed25519` today; carried explicitly so a handset never has to assume. */
    algorithm: z.literal('Ed25519'),
    /** Base64 SPKI. The public half, and nothing else. */
    publicKeySpki: z.string().min(1),
    status: gatePackageSigningKeyStatusSchema,
});
/**
 * The whole current keyring, as one value.
 *
 * Sent complete rather than as a delta, and that is deliberate: a handset that
 * received "key X is now compromised" as an increment has to have received every
 * earlier increment to be correct, and a handset that was switched off during
 * one of them would carry a trust policy nobody can reconstruct. A complete
 * keyring is idempotent, order-independent, and correct on first contact after
 * any amount of absence.
 *
 * A key absent from the keyring is not "unchanged". It is a key this handset
 * has no basis to trust.
 */
export const gatePackageSigningKeyringSchema = z.object({
    keys: z.array(gatePackageSigningKeySchema),
});
/**
 * Whether a key in this status may verify a package signature.
 *
 * One place, so the cloud, the Edge and the handset cannot drift into three
 * opinions about what RETIRED means. Expressed as "may verify" rather than "is
 * valid", because the question a verifier asks is narrower than trust in
 * general: signing authority is a separate matter and is not decided here.
 */
export function mayVerifyGatePackages(status) {
    switch (status) {
        case 'ACTIVE':
        case 'RETIRED':
            return true;
        case 'COMPROMISED':
            return false;
    }
}
//# sourceMappingURL=gate-package-provisioning-schemas.js.map